import { useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { css, styled } from 'goober';
import { useTranslation } from 'react-i18next';
import type { FetchNextPageOptions } from '@tanstack/react-query';

import type { RequestProjectResponse } from '@/api/v4/request-project.api';
import { theme } from '@/theme/setupTheme';
import {
  RequestedFromCell,
  DescriptionCell,
  DateCell,
  ActionsCell,
  BusinessUnitCell,
} from './pending-request-cells';

interface PendingRequestsInfiniteTableProps {
  hasMore: boolean;
  height: string;
  isLoading: boolean;
  onLoadMore: (options?: FetchNextPageOptions) => Promise<unknown>;
  pendingRequests: RequestProjectResponse[];
  scrollableTarget?: string;
}

export const PendingRequestsInfiniteTable = ({
  pendingRequests,
  isLoading,
  hasMore,
  onLoadMore,
  height,
  scrollableTarget,
}: PendingRequestsInfiniteTableProps) => {
  const { t } = useTranslation('projects');

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || !onLoadMore) {
      return;
    }
    await onLoadMore();
  }, [hasMore, onLoadMore]);

  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCellNarrow>
          <Skeleton />
        </TableCellNarrow>
        <TableCellNarrow>
          <Skeleton />
        </TableCellNarrow>
        <TableCellWide>
          <Skeleton />
        </TableCellWide>
        <TableCellNarrow>
          <Skeleton />
        </TableCellNarrow>
        <TableCellNarrow>
          <Skeleton />
        </TableCellNarrow>
      </TableRow>
    ));
  };

  return (
    <InfiniteScroll
      dataLength={pendingRequests.length}
      next={handleLoadMore}
      hasMore={hasMore}
      loader={null}
      height={scrollableTarget ? undefined : height}
      scrollableTarget={scrollableTarget}
      className={ScrollClassName}
    >
      <Table>
        <thead>
          <tr>
            <TableHeaderNarrow>{t`projects.pendingRequests.requestedFrom`}</TableHeaderNarrow>
            <TableHeaderNarrow>{t`projects.requestProjectForm.businessUnit`}</TableHeaderNarrow>
            <TableHeaderWide>{t`projects.requestProjectForm.description`}</TableHeaderWide>
            <TableHeaderNarrow>{t`projects.pendingRequests.dateRequested`}</TableHeaderNarrow>
            <TableHeaderNarrow></TableHeaderNarrow>
          </tr>
        </thead>
        <tbody>
          {isLoading && pendingRequests.length === 0
            ? renderSkeletonRows()
            : pendingRequests.map((request, index) => (
                <TableRow
                  key={request.id}
                  isLast={index === pendingRequests.length - 1}
                >
                  <TableCellNarrow>
                    <RequestedFromCell request={request} />
                  </TableCellNarrow>
                  <TableCellNarrow>
                    <BusinessUnitCell businessUnit={request.businessUnit} />
                  </TableCellNarrow>
                  <TableCellWide>
                    <DescriptionCell description={request.description} />
                  </TableCellWide>
                  <TableCellNarrow>
                    <DateCell date={new Date(request.createdDate)} />
                  </TableCellNarrow>
                  <TableCellNarrow>
                    <ActionsCell requestId={request.id} />
                  </TableCellNarrow>
                </TableRow>
              ))}
        </tbody>
      </Table>
    </InfiniteScroll>
  );
};

const Table = styled('table')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  width: calc(100% - 15px);
  position: relative;
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.table};
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHeader = styled('th')`
  padding: 0.75em 1.5em;
  text-align: left;
  background-color: ${({ theme }) => theme.colors.gray.c1};
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-family: ${({ theme }) => theme.fonts.inter};
  color: ${({ theme }) => theme.colors.gray.c12};
  font-size: 12px;
  line-height: 145%;
  letter-spacing: 0.03em;
`;

const TableHeaderNarrow = styled(TableHeader)`
  width: 15%;
  min-width: 120px;
  padding-left: 1.5em;
  padding-right: 1em;
`;

const TableHeaderWide = styled(TableHeader)`
  width: 40%;
  min-width: 300px;
  padding-left: 1.5em;
  padding-right: 1.5em;
`;

const TableRow = styled('tr')<{ isLast?: boolean }>`
  height: 76px;
  border-bottom: ${({ isLast }) => (!isLast ? '1px solid' : 'none')};
  border-color: ${({ theme }) => theme.colors.gray.c7};
  background-color: ${({ theme }) => theme.colors.basics.white};

  &:hover {
    background-color: #edf4ff;
    cursor: pointer;
  }
`;

const TableCell = styled('td')`
  padding: 0.75em 1.5em;
  vertical-align: middle;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-family: ${({ theme }) => theme.fonts.inter};
  color: ${({ theme }) => theme.colors.basics.black};
`;

const TableCellNarrow = styled(TableCell)`
  width: 15%;
  min-width: 120px;
  padding-left: 1.5em;
  padding-right: 1em;
`;

const TableCellWide = styled(TableCell)`
  width: 40%;
  min-width: 300px;
  padding-left: 1.5em;
  padding-right: 1.5em;
`;

const Skeleton = styled('div')`
  height: 16px;
  background: ${({ theme }) => theme.colors.gray.c4};
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const ScrollClassName = css`
  ${theme.mixins.scrollbar}
`;
